import clsx from 'clsx';
import { Search } from 'lucide-react';
import { useEffect, useId, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useSearch } from '../api/hooks.ts';
import type { PartialDate, PersonLite, Qualifier, Sex } from '../api/types.ts';
import { t } from '../i18n/index.ts';
import { msg } from '../i18n/labels.ts';
import { nameOf, spanOf } from '../lib/format.ts';
import { PersonAvatar } from './PersonAvatar.tsx';
import { Input, Segmented, Select } from './ui/index.tsx';

/** Fila compacta de persona: retrato, nombre y años. */
export function PersonRow({ person, treeId, to, onClick, trailing, sub, size = 40 }: {
  person: PersonLite;
  treeId: string;
  to?: string;
  onClick?: () => void;
  trailing?: ReactNode;
  sub?: ReactNode;
  size?: number;
}) {
  const body = (
    <>
      <PersonAvatar person={person} treeId={treeId} size={size} />
      <span className="min-w-0 flex-1">
        <span className="block truncate font-serif text-[15.5px] font-semibold leading-tight">{nameOf(person)}</span>
        <span className="block truncate text-[13px] text-ink-faint">{sub ?? spanOf(person)}</span>
      </span>
      {trailing}
    </>
  );
  const cls = 'flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left transition-colors hover:bg-surface-2';
  if (to) return <Link to={to} className={cls}>{body}</Link>;
  if (onClick) return <button type="button" onClick={onClick} className={cls}>{body}</button>;
  return <div className={clsx(cls, 'hover:bg-transparent')}>{body}</div>;
}

/** Autocompletado para elegir una persona existente del árbol. */
export function PersonPicker({ treeId, onPick, label, exclude = [], autoFocus }: {
  treeId: string;
  onPick: (p: PersonLite) => void;
  label?: string;
  exclude?: string[];
  autoFocus?: boolean;
}) {
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const listId = useId();
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 220);
    return () => clearTimeout(t);
  }, [q]);
  const res = useSearch(treeId, { q: debounced, limit: 8 }, debounced.length >= 2);
  const items = (res.data?.items ?? []).filter((p) => !exclude.includes(p.id));
  return (
    <div className="relative">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3.5 top-[38px] size-4 text-ink-faint" aria-hidden />
        <Input
          label={label ?? t('Buscar persona en el árbol')}
          value={q}
          autoFocus={autoFocus}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('Nombre o apellido')}
          className="pl-10"
          role="combobox"
          aria-expanded={items.length > 0}
          aria-controls={listId}
          aria-autocomplete="list"
        />
      </div>
      {debounced.length >= 2 && (
        <ul id={listId} role="listbox" className="mt-2 flex max-h-72 flex-col overflow-y-auto rounded-2xl border border-line bg-surface p-1">
          {items.length === 0 && !res.isFetching && <li className="px-3 py-3 text-[14px] text-ink-faint">{t('Sin coincidencias para “{query}”.', { query: debounced })}</li>}
          {items.map((p) => (
            <li key={p.id} role="option" aria-selected={false}>
              <PersonRow person={p} treeId={treeId} onClick={() => onPick(p)} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export interface QuickPersonValue {
  givenNames: string;
  paternalSurname: string;
  maternalSurname: string;
  sex: Sex;
  birthYear: string;
  isLiving?: boolean;
}

export const emptyQuickPerson = (sex: Sex = 'U'): QuickPersonValue => ({ givenNames: '', paternalSurname: '', maternalSurname: '', sex, birthYear: '' });

export function quickToApi(v: QuickPersonValue) {
  const year = Number(v.birthYear);
  return {
    givenNames: v.givenNames.trim(),
    paternalSurname: v.paternalSurname.trim() || null,
    maternalSurname: v.maternalSurname.trim() || null,
    sex: v.sex,
    birthYear: v.birthYear && Number.isInteger(year) ? year : null,
    ...(v.isLiving !== undefined ? { isLiving: v.isLiving } : {}),
  };
}

type LivingChoice = 'vive' | 'fallecio' | 'nose';
const toChoice = (v: boolean | null): LivingChoice => (v === true ? 'vive' : v === false ? 'fallecio' : 'nose');
const fromChoice = (c: LivingChoice): boolean | null => (c === 'vive' ? true : c === 'fallecio' ? false : null);

/** ¿Vive? con tres respuestas: vive, falleció o no se sabe (null). */
export function LivingField({ value, onChange }: { value: boolean | null; onChange: (v: boolean | null) => void }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-ink-soft">{t('¿Vive?')}</span>
      <Segmented<LivingChoice>
        label={t('¿La persona vive?')}
        value={toChoice(value)}
        onChange={(c) => onChange(fromChoice(c))}
        options={[{ value: 'vive', label: t('Vive') }, { value: 'fallecio', label: t('Falleció') }, { value: 'nose', label: t('No se sabe') }]}
      />
    </div>
  );
}

/** Campos de nombre hispano completo + sexo + año de nacimiento. */
export function QuickPersonFields({ value, onChange, showSex = true, autoFocus, yearLabel }: {
  value: QuickPersonValue;
  onChange: (v: QuickPersonValue) => void;
  showSex?: boolean;
  autoFocus?: boolean;
  yearLabel?: string;
}) {
  const set = (k: keyof QuickPersonValue) => (e: { target: { value: string } }) => onChange({ ...value, [k]: e.target.value });
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Input label={t('Nombres')} required autoFocus={autoFocus} autoComplete="off" value={value.givenNames} onChange={set('givenNames')} wrapClassName="sm:col-span-2" maxLength={200} />
      <Input label={t('Apellido paterno')} autoComplete="off" value={value.paternalSurname} onChange={set('paternalSurname')} maxLength={120} />
      <Input label={t('Apellido materno')} autoComplete="off" value={value.maternalSurname} onChange={set('maternalSurname')} maxLength={120} />
      <Input
        label={yearLabel ?? t('Año de nacimiento')}
        inputMode="numeric"
        pattern="[0-9]{3,4}"
        placeholder={t('Ej. 1932')}
        value={value.birthYear}
        onChange={(e) => onChange({ ...value, birthYear: e.target.value.replace(/\D/g, '').slice(0, 4) })}
        hint={t('Con el año basta.')}
      />
      {showSex && (
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-ink-soft">{t('Sexo')}</span>
          <Segmented<Sex>
            label={t('Sexo')}
            value={value.sex}
            onChange={(sex) => onChange({ ...value, sex })}
            options={[{ value: 'M', label: t('Hombre') }, { value: 'F', label: t('Mujer') }, { value: 'U', label: t('No sé') }]}
          />
        </div>
      )}
    </div>
  );
}

const MONTHS = [
  msg('enero'), msg('febrero'), msg('marzo'), msg('abril'), msg('mayo'), msg('junio'),
  msg('julio'), msg('agosto'), msg('septiembre'), msg('octubre'), msg('noviembre'), msg('diciembre'),
];

/** Fecha parcial: año obligatorio si hay fecha; mes, día y calificador opcionales. */
/** Lo que impide guardar una fecha parcial, o null si está bien (incluida la fecha vacía). */
export function partialDateError(d: PartialDate | null | undefined): string | null {
  if (!d) return null;
  if ((d.month || d.day) && !d.year) return t('Falta el año: sin él no se puede guardar el día ni el mes.');
  if (d.day && !d.month) return t('Elige también el mes.');
  if (d.year && d.month && d.day && d.day > new Date(Date.UTC(d.year, d.month, 0)).getUTCDate()) return t('{month} de {year} no tiene {day} días.', { month: t(MONTHS[d.month - 1]!), year: d.year, day: d.day });
  return null;
}

/** Fecha en el orden de lectura (día, mes, año) con su precisión. Ningún campo se bloquea: lo incompleto se explica debajo. */
export function PartialDateInput({ value, onChange, label }: { value: PartialDate | null; onChange: (v: PartialDate | null) => void; label: string }) {
  const v = value ?? {};
  const update = (patch: Partial<PartialDate>) => {
    const next = { ...v, ...patch };
    onChange(next.year || next.month || next.day ? next : next.qualifier && next.qualifier !== 'exact' ? next : null);
  };
  const digits = (raw: string, max: number) => Number(raw.replace(/\D/g, '').slice(0, max)) || null;
  const problem = partialDateError(value);
  return (
    <fieldset className="flex flex-col gap-1.5">
      <legend className="mb-1.5 text-sm font-medium text-ink-soft">{label}</legend>
      <div className="grid grid-cols-[0.8fr_1.5fr_1fr] gap-2 sm:grid-cols-[0.8fr_1.5fr_1fr_1.3fr]">
        <Input aria-label={t('{label}: día', { label })} placeholder={t('Día')} inputMode="numeric" maxLength={2} value={v.day ?? ''} onChange={(e) => update({ day: digits(e.target.value, 2) && Math.min(31, digits(e.target.value, 2)!) })} />
        <Select aria-label={t('{label}: mes', { label })} value={v.month ?? ''} onChange={(e) => update({ month: e.target.value ? Number(e.target.value) : null })}>
          <option value="">{t('Mes')}</option>
          {MONTHS.map((m, i) => <option key={m} value={i + 1}>{t(m)}</option>)}
        </Select>
        <Input aria-label={t('{label}: año', { label })} placeholder={t('Año')} inputMode="numeric" maxLength={4} value={v.year ?? ''} onChange={(e) => update({ year: digits(e.target.value, 4) })} />
        <Select aria-label={t('{label}: precisión', { label })} wrapClassName="col-span-3 sm:col-span-1" value={v.qualifier ?? 'exact'} onChange={(e) => update({ qualifier: e.target.value as Qualifier })}>
          <option value="exact">{t('Exacta')}</option>
          <option value="about">{t('Aproximada')}</option>
          <option value="before">{t('Antes de')}</option>
          <option value="after">{t('Después de')}</option>
        </Select>
      </div>
      <p className={clsx('text-[13px]', problem ? 'text-danger' : 'text-ink-faint')} aria-live="polite">
        {problem ?? t('Con el año basta; el día y el mes son opcionales.')}
      </p>
    </fieldset>
  );
}
